import { Grid } from '@material-ui/core';
import React, { Suspense, useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import { UserRoleDto } from '../../swagger';
import LoadingPanel from '../../ui/old/Feedback/LoadingPanel';
import LoadingIndicator from '../../ui/old/Feedback/LoadingIndicator';
import { SecurityModule } from './components/SecurityModule';
import { SECURITY_MODULE_ID } from './config';
import { HttpResponseStatus, SecurityRights } from './enums';
import useAdGroupsDataScopes from './hooks/adGroupsDataScopes/useAdGroupsDataScopes';
import useAdGroupsDataScopesByModuleId from './hooks/adGroupsDataScopes/useAdGroupsDataScopesByModuleId';
import useAdGroups from './hooks/useAdGroups';
import useGroupsByRoleId from './hooks/useGroupsByRoleId';
import useRoleByModuleId from './hooks/useRoleByModuleId';
import useStylesGrid from './styles/grid.styles';
import useSecurityQuery from './useSecurityQuery';
import { sortItemsByName } from './services/AdGroupsService';
import { useQueryClient } from 'react-query';
import { useToastify } from '../../Hooks';

const SecurityRole = React.lazy(() => import('./components/SecurityRole/SecurityRole'));
const SecurityGroups = React.lazy(() => import('./components/SecurityGroups/SecurityGroups'));
const SecurityDataScopeGroups = React.lazy(
    () => import('./components/SecurityDataScopeGroups/SecurityDataScopeGroups'),
);

const Security = () => {
    const { data, isLoading: isLoadingSecurity } = useSecurityQuery('Security');
    const queryClient = useQueryClient();
    const [securityModule, setSecurityModule] = useState();
    const [securityRole, setSecurityRole] = useState();
    const {
        data: securityRoles,
        error: errorRoleByModuleId,
        isLoading: isLoadingRoleByModuleId,
    } = useRoleByModuleId(securityModule);
    const securityStyles = useStylesGrid();
    const { showToastError } = useToastify();

    const {
        data: groupsByRoleId,
        error: errorGroupsByRoleId,
        isLoading: isLoadingUseGroupsByRoleId,
    } = useGroupsByRoleId(securityRole, {
        enabled: securityRole,
        onError(error: any) {
            showToastError(error.message);
        },
    });
    const { data: allAdGroups, error: errorAdGroups } = useAdGroups({
        enabled: securityRole,
        onError(error: any) {
            showToastError(error.message);
        },
    });
    const {
        data: adGroupsByModuleId,
        allParameters,
        error: errorAdGroupsDataScopesByModuleId,
    } = useAdGroupsDataScopesByModuleId(securityModule as any, {
        onError(error: any) {
            showToastError(error.message);
        },
    });

    useEffect(() => {
        return () => {
            queryClient.cancelQueries('adgroups-module-');
            queryClient.cancelQueries('roles-module-');
        };
    }, [securityModule]);

    const { data: gridDataScopes, error: errorAdGroupsDataScope } = useAdGroupsDataScopes(
        securityRole as any,
        allAdGroups,
    );

    const allSecurityGroups = sortItemsByName(
        groupsByRoleId?.groups?.map((group) => {
            return allAdGroups?.find((adgroup) => adgroup.id == group);
        }),
    );

    const allDataScopeSecurityGroups = sortItemsByName(
        groupsByRoleId?.dataScopeGroups?.map((group) => {
            return allAdGroups?.find((adgroup) => adgroup.id == group);
        }),
    );
    const allSelectedSecurityGroups = sortItemsByName(
        groupsByRoleId?.usedGroups?.map((group) => {
            return allAdGroups?.find((adgroup) => adgroup.id == group);
        }),
    );

    const isSecurityAdmin = (element: UserRoleDto) =>
        element.roleId == SecurityRights.Admin && element.module!.moduleId == SECURITY_MODULE_ID;

    if (isLoadingSecurity) {
        return <LoadingPanel />;
    }
    if (data && !data?.some(isSecurityAdmin)) {
        return (
            <Redirect
                to={{
                    pathname: '/access-denied',
                }}
            />
        );
    } else if (
        errorGroupsByRoleId?.message.includes(HttpResponseStatus.Unauthorized.toString()) ||
        errorAdGroups?.message.includes(HttpResponseStatus.Unauthorized.toString()) ||
        errorAdGroupsDataScope?.message.includes(HttpResponseStatus.Unauthorized.toString()) ||
        errorAdGroupsDataScope?.message.includes(HttpResponseStatus.Unauthorized.toString()) ||
        errorAdGroupsDataScopesByModuleId?.message.includes(HttpResponseStatus.Unauthorized.toString()) ||
        errorRoleByModuleId?.message.includes(HttpResponseStatus.Unauthorized.toString())
    )
        return (
            <Redirect
                to={{
                    pathname: '/access-denied',
                }}
            />
        );
    else
        return (
            <>
                <Grid container className={securityStyles.grid}>
                    <Grid item xs={12}>
                        <SecurityModule setSecurityModule={setSecurityModule} setSecurityRole={setSecurityRole} />
                    </Grid>
                    {securityRoles && (
                        <Grid item xs={4} style={{ marginLeft: '2%' }}>
                            <Suspense fallback={<LoadingIndicator />}>
                                <SecurityRole setSecurityRole={setSecurityRole} data={securityRoles} />
                            </Suspense>
                        </Grid>
                    )}
                    {isLoadingRoleByModuleId && <LoadingPanel />}
                    {allAdGroups && securityRole && adGroupsByModuleId && allParameters && gridDataScopes && (
                        <Grid item xs={6} style={{ marginLeft: '3%' }}>
                            <Suspense fallback={<LoadingIndicator />}>
                                <SecurityGroups
                                    groupIds={allSelectedSecurityGroups}
                                    allAdGroups={allSecurityGroups}
                                    securityRole={securityRole}
                                />
                                <SecurityDataScopeGroups
                                    allAdGroups={allDataScopeSecurityGroups}
                                    allParameters={allParameters}
                                    adGroupsByModuleId={adGroupsByModuleId}
                                    gridDataScopes={gridDataScopes}
                                    securityRole={securityRole}
                                />
                            </Suspense>
                        </Grid>
                    )}
                    {isLoadingUseGroupsByRoleId && <LoadingPanel />}
                </Grid>
            </>
        );
};

export default Security;
